// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — multi-workspace registry.
//
// Lets the SPA hold N independent vaults side-by-side. "personal" and "work"
// can each have their own folder on disk + their own Dexie DB. The topbar's
// workspace switcher reads + writes through the singleton accessor exported
// at the bottom of this file (`getWorkspacesManager()`).
//
// What lives where:
//   - Per-workspace docs / embeddings / yjs / edges        → `tether-brain-v2-<id>` Dexie DB
//   - Workspace registry (id, name, createdAt, lastActive) → `tether-brain-v2-workspaces` Dexie DB
//   - Per-workspace FSAccess folder handle                 → `tether-brain-v2-fsacc`/handle::<id> in IDB
//   - Active workspace id (sticky across reloads)          → localStorage `tether.activeWorkspaceId`
//
// Migration: on first `init()` with no rows in the registry, if the legacy
// single-DB `tether-brain-v2` has any docs, we register it as a workspace
// `{ id: 'default', name: 'brain' }` so existing users keep their data without
// touching anything. The legacy `tether-brain-v2-fsacc` `vault-root` handle
// (if present) gets migrated to the new keyed slot for the `default` workspace.
//
// MCP integration: PURELY client-side for now. The MCP server reads `~/brain`
// regardless of the active workspace. We pin a TODO at the chat-fetch layer
// to forward `X-Workspace-Id` once the server learns to namespace by it.

import Dexie, { type EntityTable } from 'dexie';
import {
  openNamedDB,
  DB_NAME as LEGACY_DB_NAME,
  type TetherDB,
} from './dexie-schema';
import { VaultStore, setVaultStoreResolver } from './vault-store';
import {
  putHandleForWorkspace,
  deleteHandleForWorkspace,
  getRawLegacyHandle,
  type VaultDirHandle,
} from './fsacc';

// ─── types ──────────────────────────────────────────────────────────────────

export interface Workspace {
  /** ULID-ish stable id. The legacy single-DB migrates as `id: 'default'`. */
  id: string;
  /** Display name shown in the topbar dropdown. Free-form, user-editable. */
  name: string;
  /** ms epoch when the workspace was registered. */
  createdAt: number;
  /** ms epoch the user last switched into this workspace. */
  lastActive: number;
  /**
   * Resolved disk path for this workspace's vault folder, when known. Used by
   * the MCP server (via the `X-Workspace-Path` header) to resolve which vault
   * a given chat turn should read from.
   *
   * Browsers do NOT expose an absolute filesystem path for a
   * `FileSystemDirectoryHandle` chosen via `showDirectoryPicker()` — the
   * sandbox forbids it. So this field starts as the directory `name` (the
   * folder leaf, no parent path) and can be upgraded to a full path via
   * `setActiveFolderPath()` when the user types it manually in Settings.
   *
   * Optional for backward compat with rows written before this field existed.
   */
  folderPath?: string;
}

interface WorkspaceRow extends Workspace {}

/** Internal cached state per workspace. We keep VaultStore + folder handle
 *  in memory once anyone touches them so subsequent reads are O(1). */
interface WorkspaceCacheEntry {
  store?: VaultStore;
  fsHandle?: VaultDirHandle | null;
}

// ─── registry DB (separate from per-workspace DBs) ──────────────────────────

const REGISTRY_DB_NAME = 'tether-brain-v2-workspaces';
const REGISTRY_DB_VERSION = 1;
const ACTIVE_LS_KEY = 'tether.activeWorkspaceId';
const DEFAULT_WORKSPACE_ID = 'default';

class WorkspacesRegistryDB extends Dexie {
  declare workspaces: EntityTable<WorkspaceRow, 'id'>;

  constructor(name: string = REGISTRY_DB_NAME) {
    super(name);
    this.version(REGISTRY_DB_VERSION).stores({
      workspaces: 'id, name, createdAt, lastActive',
    });
  }
}

// ─── ULID-ish id ────────────────────────────────────────────────────────────
//
// Crockford-base32 timestamp (10 chars) + 16 random chars = 26 chars total,
// lexicographically sortable by creation time. Good enough for our purposes;
// no need to pull in `ulid` as a dependency.

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function generateULID(): string {
  let ts = Date.now();
  let timePart = '';
  for (let i = 0; i < 10; i++) {
    timePart = CROCKFORD[ts & 31]! + timePart;
    ts = Math.floor(ts / 32);
  }
  let randPart = '';
  // 16 chars × 5 bits = 80 bits of randomness.
  for (let i = 0; i < 16; i++) {
    randPart += CROCKFORD[Math.floor(Math.random() * 32)];
  }
  return timePart + randPart;
}

function dbNameFor(id: string): string {
  return id === DEFAULT_WORKSPACE_ID
    ? LEGACY_DB_NAME
    : `${LEGACY_DB_NAME}-${id}`;
}

// ─── localStorage helpers (SSR-safe) ────────────────────────────────────────

function lsGet(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function lsSet(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* private mode / SSR — silently no-op */
  }
}

function lsRemove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* ignore */
  }
}

// ─── manager ────────────────────────────────────────────────────────────────

export interface WorkspaceChangeEvent {
  type: 'create' | 'rename' | 'delete' | 'switch' | 'reset';
  workspaceId: string | null;
}

type Listener = (e: WorkspaceChangeEvent) => void;

export class WorkspacesManager {
  private registryDb: WorkspacesRegistryDB;
  /** In-memory mirror of the registry rows — kept in sync on every mutation. */
  private rows: Map<string, Workspace> = new Map();
  /** Lazy per-workspace cache (VaultStore + folder handle). */
  private cache: Map<string, WorkspaceCacheEntry> = new Map();
  private listeners: Set<Listener> = new Set();
  private inited = false;
  private initPromise: Promise<void> | null = null;

  constructor(registryDbName: string = REGISTRY_DB_NAME) {
    this.registryDb = new WorkspacesRegistryDB(registryDbName);
  }

  /** Load the registry + run the legacy → default migration. Idempotent. */
  async init(): Promise<void> {
    if (this.inited) return;
    if (this.initPromise) return this.initPromise;
    this.initPromise = (async () => {
      const all = await this.registryDb.workspaces.toArray();
      this.rows = new Map(all.map((r) => [r.id, r]));

      if (this.rows.size === 0) {
        await this.migrateLegacyIfPresent();
      }
      this.inited = true;
    })();
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * If the legacy single-DB `tether-brain-v2` has docs (or even just exists
   * with a granted FSAccess handle), register it as `id: 'default'` named
   * "brain" so the user doesn't lose their vault on the multi-workspace
   * upgrade.
   */
  private async migrateLegacyIfPresent(): Promise<void> {
    let shouldMigrate = false;

    // Probe for docs in the legacy DB. We open it by its canonical name.
    try {
      const legacyDb = openNamedDB(LEGACY_DB_NAME);
      const count = await legacyDb.docs.count();
      if (count > 0) shouldMigrate = true;
      legacyDb.close();
    } catch {
      /* legacy DB missing or unreadable — fine */
    }

    // Even if no docs, an existing FSAccess handle means the user picked a
    // folder once; preserve their choice.
    if (!shouldMigrate) {
      try {
        const legacy = await getRawLegacyHandle();
        if (legacy) shouldMigrate = true;
      } catch {
        /* ignore */
      }
    }

    if (!shouldMigrate) return;

    const now = Date.now();
    // Seed `folderPath` from the legacy handle's leaf name when available
    // (best identity we can offer without absolute-path access). The MCP
    // server treats this as a hint and validates / falls back to env
    // VAULT_PATH on every request.
    let legacyFolderPath: string | undefined;
    try {
      const legacy = await getRawLegacyHandle();
      if (legacy && typeof legacy.name === 'string' && legacy.name.length > 0) {
        legacyFolderPath = legacy.name;
      }
    } catch {
      /* legacy handle unavailable — fine */
    }

    const row: Workspace = {
      id: DEFAULT_WORKSPACE_ID,
      name: 'brain',
      createdAt: now,
      lastActive: now,
      ...(legacyFolderPath ? { folderPath: legacyFolderPath } : {}),
    };
    await this.registryDb.workspaces.put(row);
    this.rows.set(row.id, row);

    // Migrate the FSAccess handle from the legacy single key to the keyed slot.
    try {
      const legacy = await getRawLegacyHandle();
      if (legacy) {
        await putHandleForWorkspace(DEFAULT_WORKSPACE_ID, legacy);
      }
    } catch {
      /* don't block migration on handle persistence */
    }

    // No active id in localStorage yet? Default to the migrated workspace.
    if (!lsGet(ACTIVE_LS_KEY)) lsSet(ACTIVE_LS_KEY, DEFAULT_WORKSPACE_ID);
  }

  // ─── reads ────────────────────────────────────────────────────────────────

  /** Synchronous snapshot of the registry. Sorted by `createdAt` asc so the
   *  topbar list order matches creation order (predictable for the user). */
  list(): Workspace[] {
    return Array.from(this.rows.values()).sort(
      (a, b) => a.createdAt - b.createdAt,
    );
  }

  /** Read the active workspace id from localStorage. Returns null if no
   *  workspaces have been registered yet. */
  getActiveId(): string | null {
    const id = lsGet(ACTIVE_LS_KEY);
    if (id && this.rows.has(id)) return id;
    // Sticky fallback: if rows exist but the LS pointer is stale, pick the
    // most-recently-active row so the UI doesn't show "no workspace".
    const all = this.list();
    if (all.length === 0) return null;
    const sorted = [...all].sort((a, b) => b.lastActive - a.lastActive);
    return sorted[0]?.id ?? null;
  }

  get(id: string): Workspace | undefined {
    return this.rows.get(id);
  }

  /**
   * Folder path bound to a specific workspace, if any. Returns the persisted
   * `folderPath` row field — typically the directory leaf-name set at
   * `create()` time, or a full disk path written via `setFolderPath()` from
   * the Settings panel's manual-path input.
   *
   * Caveat: browsers do not let us derive an absolute path from a
   * FileSystemDirectoryHandle (sandboxing). For the MCP server to actually
   * resolve to the right vault, the user must either:
   *   1. Type the path explicitly via Settings (advanced flow), or
   *   2. Run the MCP server with `VAULT_PATH` set per workspace (single-vault
   *      fallback — current behaviour).
   * The `X-Workspace-Path` header that the SPA sends is BEST-EFFORT; the
   * server validates and falls back to env `VAULT_PATH` when the header is
   * missing or the path doesn't pass validation.
   */
  getFolderPath(id: string): string | null {
    const row = this.rows.get(id);
    return row?.folderPath ?? null;
  }

  /**
   * Folder path of the currently active workspace. Returns null when no
   * workspace is active yet (first boot before `create()`) or when the
   * active workspace was registered without a path. Async to leave room for
   * a future implementation that resolves a sandboxed handle through a
   * privileged bridge — today it just reads the in-memory row.
   */
  async getActiveFolderPath(): Promise<string | null> {
    const id = this.getActiveId();
    if (!id) return null;
    return this.getFolderPath(id);
  }

  // ─── mutations ────────────────────────────────────────────────────────────

  /**
   * Switch the active workspace. Persists to localStorage and emits a
   * `switch` event so the topbar / vault hook re-render.
   */
  async setActiveId(id: string): Promise<void> {
    if (!this.rows.has(id)) {
      throw new Error(`workspace ${id} is not registered`);
    }
    lsSet(ACTIVE_LS_KEY, id);
    const row = this.rows.get(id);
    if (row) {
      const next: Workspace = { ...row, lastActive: Date.now() };
      await this.registryDb.workspaces.put(next);
      this.rows.set(id, next);
    }
    this.emit({ type: 'switch', workspaceId: id });
  }

  /**
   * Create a new workspace, persist its FSAccess handle (optional), and
   * spin up a fresh per-workspace Dexie DB. The new workspace is NOT made
   * active automatically — the caller decides (the switcher UI typically
   * does call `setActiveId` right after).
   */
  async create(
    name: string,
    dirHandle?: FileSystemDirectoryHandle | VaultDirHandle | null,
    opts: { folderPath?: string } = {},
  ): Promise<Workspace> {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('workspace name cannot be empty');

    const id = generateULID();
    const now = Date.now();

    // Capture the folder identity as best we can. Priority:
    //   1. Caller-supplied `folderPath` (e.g. typed in a Settings input).
    //   2. The directory handle's `name` (a leaf folder name, no parent).
    //   3. Undefined — workspace has no folder bound yet.
    // The MCP server treats this as opaque text and validates on every
    // request; a leaf-name fallback won't pass validation but at least lets
    // us record what the user picked.
    let folderPath: string | undefined = opts.folderPath?.trim() || undefined;
    if (!folderPath && dirHandle) {
      const handle = dirHandle as { name?: string };
      if (typeof handle.name === 'string' && handle.name.length > 0) {
        folderPath = handle.name;
      }
    }

    const row: Workspace = {
      id,
      name: trimmed,
      createdAt: now,
      lastActive: now,
      ...(folderPath ? { folderPath } : {}),
    };
    await this.registryDb.workspaces.put(row);
    this.rows.set(id, row);

    if (dirHandle) {
      await putHandleForWorkspace(id, dirHandle as VaultDirHandle);
      // Cache the handle for the in-memory store.
      this.cache.set(id, { fsHandle: dirHandle as VaultDirHandle });
    }

    // Touch the per-workspace DB so it shows up in IDB inspector even before
    // the first write. Cheap — Dexie defers IDB open until the first op,
    // so opening + closing here is essentially a no-op.
    try {
      const db = openNamedDB(dbNameFor(id));
      // Don't await an actual op; the open happens lazily on first use.
      db.close();
    } catch {
      /* ignore — first write will surface any real failure */
    }

    this.emit({ type: 'create', workspaceId: id });
    return row;
  }

  /**
   * Update the on-disk folder path bound to a workspace. Used when the user
   * types a path manually in Settings (FSAccess can't expose absolute paths,
   * so this is the supported escape hatch for cross-process path sharing
   * with the local MCP server).
   *
   * Pass an empty string / null / undefined to clear the field. The MCP
   * server falls back to env `VAULT_PATH` whenever the SPA sends no
   * `X-Workspace-Path` header (which happens when this is null).
   */
  async setFolderPath(id: string, folderPath: string | null): Promise<void> {
    const row = this.rows.get(id);
    if (!row) throw new Error(`workspace ${id} not found`);
    const trimmed = typeof folderPath === 'string' ? folderPath.trim() : '';
    const next: Workspace = { ...row };
    if (trimmed) {
      next.folderPath = trimmed;
    } else {
      delete next.folderPath;
    }
    await this.registryDb.workspaces.put(next);
    this.rows.set(id, next);
  }

  /** Convenience — set the folder path on the currently active workspace.
   *  Returns false if no workspace is active. */
  async setActiveFolderPath(folderPath: string | null): Promise<boolean> {
    const id = this.getActiveId();
    if (!id) return false;
    await this.setFolderPath(id, folderPath);
    return true;
  }

  /** Rename a workspace in place. ID and Dexie DB name don't change — only
   *  the display label. */
  async rename(id: string, newName: string): Promise<void> {
    const row = this.rows.get(id);
    if (!row) throw new Error(`workspace ${id} not found`);
    const trimmed = newName.trim();
    if (!trimmed) throw new Error('workspace name cannot be empty');
    const next: Workspace = { ...row, name: trimmed };
    await this.registryDb.workspaces.put(next);
    this.rows.set(id, next);
    this.emit({ type: 'rename', workspaceId: id });
  }

  /**
   * Unregister a workspace. By default the per-workspace Dexie DB is left on
   * disk (so the user can re-add it later). Pass `wipeData: true` to delete
   * the DB + drop the FSAccess handle.
   *
   * Refuses to delete the last remaining workspace — the topbar would have no
   * fallback. Caller should create a replacement first.
   */
  async delete(
    id: string,
    opts: { wipeData?: boolean } = {},
  ): Promise<void> {
    if (!this.rows.has(id)) return;
    if (this.rows.size <= 1) {
      throw new Error('refusing to delete the last remaining workspace');
    }

    // Drop the cache + close the live VaultStore (if any) so the DB isn't
    // held open by a stale connection.
    const entry = this.cache.get(id);
    if (entry?.store) {
      // VaultStore doesn't expose a `close()` because it shares a DB with
      // sibling modules in the single-vault world. In multi-vault land we
      // tear down the connection by deleting the DB itself below. The store
      // becomes orphaned — fine because we drop the cache entry.
    }
    this.cache.delete(id);

    if (opts.wipeData) {
      // Drop the Dexie DB.
      try {
        await Dexie.delete(dbNameFor(id));
      } catch {
        /* ignore — best-effort */
      }
      // Drop the persisted FSAccess handle.
      try {
        await deleteHandleForWorkspace(id);
      } catch {
        /* ignore */
      }
    }

    await this.registryDb.workspaces.delete(id);
    this.rows.delete(id);

    // If we just deleted the active workspace, redirect the LS pointer to
    // the most-recently-active survivor.
    if (lsGet(ACTIVE_LS_KEY) === id) {
      const survivor = this.list().sort(
        (a, b) => b.lastActive - a.lastActive,
      )[0];
      if (survivor) lsSet(ACTIVE_LS_KEY, survivor.id);
      else lsRemove(ACTIVE_LS_KEY);
    }

    this.emit({ type: 'delete', workspaceId: id });
  }

  // ─── per-workspace store ──────────────────────────────────────────────────

  /**
   * Returns the VaultStore for `id`, lazily constructed against the per-
   * workspace Dexie DB. Cached for the lifetime of the manager so repeated
   * topbar renders don't churn IDB connections.
   *
   * NOTE: caller is responsible for `await store.init()` before reads —
   * matches the existing single-vault contract.
   */
  getStore(id: string): VaultStore {
    let entry = this.cache.get(id);
    if (!entry) {
      entry = {};
      this.cache.set(id, entry);
    }
    if (!entry.store) {
      const db: TetherDB = openNamedDB(dbNameFor(id));
      entry.store = new VaultStore({ db });
    }
    return entry.store;
  }

  // ─── events ───────────────────────────────────────────────────────────────

  /** Subscribe to workspace lifecycle events. Returns an unsubscribe fn. */
  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(e: WorkspaceChangeEvent): void {
    for (const l of this.listeners) {
      try {
        l(e);
      } catch {
        /* swallow — a buggy listener shouldn't break the manager */
      }
    }
    // Also dispatch a DOM event so any module-graph stranger (e.g. devtools
    // panels mounted later) can listen without importing this file.
    try {
      const target =
        typeof globalThis.dispatchEvent === 'function'
          ? (globalThis as unknown as EventTarget)
          : null;
      target?.dispatchEvent(
        new CustomEvent('tether:workspace-change', { detail: e }),
      );
    } catch {
      /* ignore — non-DOM env (bun:test) */
    }
  }
}

// ─── module-level singleton ────────────────────────────────────────────────

let _mgr: WorkspacesManager | null = null;

export function getWorkspacesManager(): WorkspacesManager {
  if (!_mgr) {
    _mgr = new WorkspacesManager();
    // Wire `getVaultStore()` to route through this manager. The resolver
    // returns `null` when no workspaces are registered yet (fresh boot) so
    // the single-vault fallback path stays viable for the first render
    // before `WorkspacesManager.init()` resolves.
    setVaultStoreResolver(() => {
      const m = _mgr;
      if (!m) return null;
      if (m.list().length === 0) return null;
      const id = m.getActiveId();
      if (!id) return null;
      return m.getStore(id);
    });
  }
  return _mgr;
}

/** Test-only — reset the singleton so a fresh registry can be opened against
 *  a unique DB name. Pair with `_resetStoreForTests()` from vault-store. */
export function _resetWorkspacesForTests(): void {
  _mgr = null;
  setVaultStoreResolver(null);
}

/** Test-only — inject a manager backed by a custom registry DB name (so each
 *  test gets a clean IDB). */
export function _setWorkspacesManagerForTests(
  registryDbName: string,
): WorkspacesManager {
  _mgr = new WorkspacesManager(registryDbName);
  setVaultStoreResolver(() => {
    const m = _mgr;
    if (!m) return null;
    if (m.list().length === 0) return null;
    const id = m.getActiveId();
    if (!id) return null;
    return m.getStore(id);
  });
  return _mgr;
}

// ─── public id constants ────────────────────────────────────────────────────

export { DEFAULT_WORKSPACE_ID, ACTIVE_LS_KEY, REGISTRY_DB_NAME, dbNameFor };

// ─── TODO(mcp): forward X-Workspace-Id header from chat-fetch ──────────────
//
// The MCP server at `apps/mcp/` currently reads `~/brain` regardless of which
// workspace the SPA is showing. For per-workspace MCP routing, the chat-fetch
// in `apps/web/src/components/ChatPanel.tsx` needs to add an
//   `X-Workspace-Id: <getWorkspacesManager().getActiveId()>`
// header on every `/api/brain/chat` POST, and the server needs a workspace
// → root-folder map. Out of scope for this stream; left as a marker.
